/**
 * `traceShapes` — raster in, simplified polygons out.
 *
 * Pipeline: threshold → 8-connected foreground components → Moore-neighbour
 * boundary tracing → Douglas-Peucker → shoelace measurements.
 *
 * Enclosed voids are deliberately NOT traced. Holes stopped mattering the day
 * the traced geometry became desi-only: a punched hole does not reduce the
 * leather a piece consumes, and on the factory's own 300 dpi scans most
 * "holes" were the pencil handwriting the model room leaves on every kalıp —
 * mis-traced as blue cutouts that silently shrank the consumption figure.
 */

import { labelComponents } from './components'
import { traceBoundary } from './contour'
import { orientRing, ringArea, ringPerimeter, simplifyRing } from './geometry'
import { binarize } from './raster'
import type { GrayRaster } from './raster'

export interface TraceOptions {
  /**
   * Smallest component, in pixels, that counts as a real piece. Anything below
   * it is dropped as scan noise: dust and scanner speckle are not saya parts.
   */
  minAreaPx: number
  /**
   * Douglas-Peucker tolerance in pixels. 0 keeps the full staircase boundary.
   * Every kept vertex is an original boundary pixel — simplification only ever
   * removes points, it never invents or moves one.
   */
  simplifyTolPx: number
}

export interface TracedShape {
  /**
   * Outer ring, pixel centres, no repeated closing vertex.
   *
   * Wound so that its signed area is **negative in pixel space** (y grows
   * down). `pxToMm` flips y, which flips the sign, so the mm ring comes out
   * counter-clockwise — the module's canonical convention (CCW outer, origin
   * bottom-left).
   */
  outer: [number, number][]
  /**
   * Area of the simplified outer polygon, by shoelace.
   *
   * This is an approximation of the pixel count in two documented ways:
   *
   * 1. The ring runs through pixel *centres*, not around the pixel *edges*, so
   *    a filled region loses roughly half its perimeter: a `W×H` rectangle
   *    measures `(W−1)·(H−1)` rather than `W·H`. The bias is always an
   *    under-estimate, always about `perimeter / 2 − 1` pixels, and therefore
   *    negligible at scan resolution (≈0.14 % on a 150 × 100 mm piece at
   *    300 dpi) but visible on a toy 20 × 20 raster.
   * 2. Douglas-Peucker cuts corners, which moves the area by up to roughly
   *    `tolerance × perimeter / 2` in either direction.
   *
   * Consumption maths must use this number (or `pxToMm`'s `netMm2`, computed
   * the same way from the same ring) rather than a pixel count, so that the
   * area always matches the geometry that gets drawn, edited and exported.
   * Enclosed voids inside the piece are ignored — see the module doc.
   */
  areaPx: number
  /** Boundary length of the closed outer ring. */
  perimeterPx: number
  /** Inclusive pixel bounds of the source component: `[minX, minY, maxX, maxY]`. */
  bboxPx: [number, number, number, number]
}

/**
 * Traces every foreground piece in a thresholded raster.
 *
 * Foreground is **bright on dark** (`value > threshold`) — the scanner lid is
 * open or black-backed and the sheet material is the bright thing.
 *
 * Shapes come back in raster order of their first pixel (top-to-bottom, then
 * left-to-right), which is stable across runs and matches how a person reads
 * the scan; a shape's index in this array is the `shapeIndex` that
 * `qualityCheck` reports against.
 *
 * @throws RangeError on a non-finite or negative option.
 */
export function traceShapes(r: GrayRaster, threshold: number, opts: TraceOptions): TracedShape[] {
  const { minAreaPx, simplifyTolPx } = opts
  if (!Number.isFinite(minAreaPx) || minAreaPx < 0) {
    throw new RangeError(`traceShapes: minAreaPx must be a non-negative number, got ${minAreaPx}`)
  }
  if (!Number.isFinite(simplifyTolPx) || simplifyTolPx < 0) {
    throw new RangeError(`traceShapes: simplifyTolPx must be a non-negative number, got ${simplifyTolPx}`)
  }

  const { width, height } = r
  const pixels = width * height
  if (pixels <= 0) return []

  const mask = binarize(r, threshold)
  const foreground = labelComponents(mask, width, height, 8)

  const shapes: TracedShape[] = []
  for (let id = 1; id <= foreground.count; id += 1) {
    if (foreground.sizes[id]! < minAreaPx) continue

    const outer = orientRing(
      simplifyRing(traceBoundary(foreground.labels, width, height, id, foreground.seeds[id]!), simplifyTolPx),
      false,
    )

    shapes.push({
      outer,
      areaPx: ringArea(outer),
      perimeterPx: ringPerimeter(outer),
      bboxPx: [foreground.minX[id]!, foreground.minY[id]!, foreground.maxX[id]!, foreground.maxY[id]!],
    })
  }

  return shapes
}
